import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { post } from '../../nucleo/api';
import { useContas } from './dados';
import { hojeLocal, invalidarFinanceiro } from './tipos';

/** Transferência entre contas (M4.6): sai de uma e entra na outra numa operação só. */
export function FormTransferencia({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  if (!aberto) return null;
  return <Corpo aoFechar={aoFechar} />;
}

function Corpo({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const contas = useContas(true);
  const [novoId] = useState(() => crypto.randomUUID());
  const opcoes = (contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: `${c.nome} (${c.moeda})` }));
  const campos: DefCampo[] = [
    { nome: 'conta_origem_id', rotulo: t('campos.contaOrigem'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes },
    { nome: 'conta_destino_id', rotulo: t('campos.contaDestino'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes },
    { nome: 'valor_centavos', rotulo: t('campos.valor'), tipo: 'dinheiro', obrigatorio: true, largura: 'meia' },
    { nome: 'data_caixa', rotulo: t('campos.dataCaixa'), tipo: 'data', obrigatorio: true, largura: 'meia' },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'texto', placeholder: t('ajuda.descricaoTransferencia') },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.transferir')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-transferencia" ocupado={contas.isPending || contas.isError}>
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{t('ajuda.transferencia')}</p>
      {contas.isError ? (
        <Erro erro={contas.error} aoTentar={() => void contas.refetch()} />
      ) : contas.isPending ? (
        <Carregando />
      ) : (
        <Formulario
          id="form-transferencia"
          campos={campos}
          inicial={{ data_caixa: hojeLocal() }}
          aoEnviar={async (v) => {
            await post(
              '/financeiro/transferencias',
              { id: novoId, conta_origem_id: v.conta_origem_id, conta_destino_id: v.conta_destino_id, valor_centavos: v.valor_centavos, data_caixa: v.data_caixa, descricao: v.descricao || undefined },
              { 'Idempotency-Key': novoId },
            );
            await invalidarFinanceiro(qc);
            toast.sucesso(tn('comum.salvo'));
            aoFechar();
          }}
        />
      )}
    </Drawer>
  );
}
